import qs from 'qs';
import { trackPromise } from 'react-promise-tracker';
import api from '../../../../../utils/API';
import { getCliente } from '../../../../../utils/client';

interface OfferFilters {
  product_ids?: string | string[];
  product_family_ids?: string | string[];
  category_level1?: string | string[];
  category_level2?: string | string[];
  category_level3?: string | string[];
  category_level4?: string | string[];
  category_level5?: string | string[];
  category_level6?: string | string[];
  category_level7?: string | string[];
  product_brands?: string | string[];
  suppliers?: string | string[];
  offer_types?: string | string[];
  offer_group_names?: string | string[];
}

interface GetProductsParams {
  storesId: string[];
  descOrProdId?: string;
  size?: number;
  page?: number;
}

interface GetOffersParams {
  id: string;
  size?: number;
  page?: number;
  [key: string]: any;
}

interface UpdateOfferInlinePayload {
  product_id: string;
  new_offer_price: number | null;
  new_offer_discount: number | null;
  new_offer_group_id: string | null;
}

interface BulkEditPayload {
  product_ids: string[];
  offer_group_id?: string;
  offer: any;
}

interface GetCampaignCompetitorsInfoParams {
  campaignId: string;
  productId: string;
  storeIds: string[];
}

const buildOfferFilters = (filters: OfferFilters = {}) => {
  const normalizeArray = (value: string | string[] | null | undefined): string[] => {
    if (value === null || value === undefined || value === '') return [];
    if (Array.isArray(value)) return value;
    return [value];
  };

  const client = getCliente();

  return {
    client: client?.nome || '',
    client_id: client?.codigo || '',
    product_ids: normalizeArray(filters.product_ids),
    product_family_ids: normalizeArray(filters.product_family_ids),
    category_level1: normalizeArray(filters.category_level1),
    category_level2: normalizeArray(filters.category_level2),
    category_level3: normalizeArray(filters.category_level3),
    category_level4: normalizeArray(filters.category_level4),
    category_level5: normalizeArray(filters.category_level5),
    category_level6: normalizeArray(filters.category_level6),
    category_level7: normalizeArray(filters.category_level7),
    product_brands: normalizeArray(filters.product_brands),
    suppliers: normalizeArray(filters.suppliers),
    offer_types: normalizeArray(filters.offer_types),
    offer_group_names: normalizeArray(filters.offer_group_names),
  };
};

export const getProductsModalOffer = (id: string, params: GetProductsParams) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${id}/products`,
      method: 'GET',
      expectToken: true,
      params: {
        client: cliente?.nome,
        storesId: params.storesId,
        descOrProdId: params.descOrProdId,
        size: params?.size || 20,
        page: params?.page || 0,
      },
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp.data),
  );
};

export const saveOffer = (id: string, data: any) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${id}/offers`,
      method: 'POST',
      expectToken: true,
      params: {
        client: cliente?.nome,
        clientId: cliente?.codigo,
      },
      data: JSON.stringify(data),
    }).then(resp => resp),
  );
};

export const updateOffer = (id: string, data: any, offerId: string) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${id}/offers/${offerId}`,
      method: 'PUT',
      expectToken: true,
      params: {
        client: cliente?.nome,
        clientId: cliente?.codigo,
      },
      data: JSON.stringify(data),
    }).then(resp => resp),
  );
};

export const updateOfferInlinePrice = (campaignId: string, payload: UpdateOfferInlinePayload) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${campaignId}/offers/product`,
      method: 'PUT',
      expectToken: true,
      params: {
        client: cliente?.nome,
        clientId: cliente?.codigo,
      },
      data: JSON.stringify(payload),
    }).then(resp => resp),
  );
};

export const getOffers = (params: GetOffersParams, filters: OfferFilters) => {
  const builtFilters = buildOfferFilters(filters);
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${params.id}/offers/campaign-products`,
      method: 'POST',
      expectToken: true,
      params: {
        size: params?.size || 20,
        page: params?.page || 0,
      },
      data: {
        ...builtFilters,
      },
    }).then(resp => resp.data),
  );
};

export const getOfferByOfferId = (id: string, offerId: string) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${id}/offers/${offerId}`,
      method: 'GET',
      expectToken: true,
      params: {
        client: cliente?.nome,
      },
    }).then(resp => resp.data),
  );
};

export const deleteProdutoOferta = (id: string, productId: string) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${id}/offers/product/${productId}`,
      method: 'DELETE',
      expectToken: true,
      params: {
        client: cliente?.nome,
        clientId: cliente?.codigo,
      },
    }).then(resp => resp.data),
  );
};

export const bulkEditOffer = (campaignId: string, payload: BulkEditPayload) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/${campaignId}/offers/modal/bulk`,
      method: 'PUT',
      expectToken: true,
      params: {
        client: cliente?.nome,
        clientId: cliente?.codigo,
      },
      data: JSON.stringify(payload),
    }).then(resp => resp),
  );
};

export const programarOferta = (id: string, status: string) => {
  const cliente = getCliente();
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/campaign/campaigns/${id}/status/${status}`,
      method: 'PATCH',
      expectToken: true,
      params: {
        clientId: cliente?.codigo,
      },
    }).then(resp => resp.data),
  );
};

export const getCampaignCompetitorsInfo = ({ campaignId, productId, storeIds }: GetCampaignCompetitorsInfoParams, loadingArea?: string) => {
  return trackPromise(
    api({
      url: `${process.env.REACT_APP_IPA_URL}/competitor/prices/campaign/${productId}`,
      method: 'GET',
      expectToken: true,
      params: {
        campaignId,
        storeIds,
      },
      paramsSerializer(params) {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    }).then(resp => resp.data),
    loadingArea,
  );
};
